/**
 * PDF viewer modal with coordinate-based phrase highlighting.
 *
 * Highlight strategy:
 * - react-pdf's onLoadSuccess gives us the PDFDocumentProxy directly
 * - We call getPage() → getTextContent() on it (no re-fetch, no worker conflict)
 * - fuzzyFind() matches the quote against concatenated text items
 * - findQuoteBBoxes() converts pdfjs transform matrices to canvas coordinates
 * - A <canvas> overlay draws yellow rects pixel-perfect over the matching items
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import {
  AlertCircle,
  Download,
  ZoomIn,
  ZoomOut,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  LucideFileText,
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import pdfWorkerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { getAuthorized } from "@/lib/fetch";
import { getPdfUrl } from "../services/api";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function extractPage(q: string | undefined): number {
  if (!q) return 1;
  const m = q.match(/\[p\.\s*(\d+)\]/i);
  return m ? parseInt(m[1], 10) : 1;
}

export function buildHighlightRegex(quote: string | undefined): RegExp | null {
  if (!quote) return null;
  const clean = quote.replace(/\[p\.\s*\d+\]/gi, "").trim();
  if (!clean) return null;
  const tokens = clean
    .split(/\s+/)
    .map((t) => t.replace(/^[^\w\d]+|[^\w\d]+$/g, ""))
    .filter((t) => t.length > 3);
  if (!tokens.length) return null;
  const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return new RegExp(`(${escaped.join("|")})`, "gi");
}

/** Normalize text for matching: lowercase, collapse whitespace, strip punctuation */
function normalizeText(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Returns true if the formatted cell value is primarily numeric
 * (amount, percentage, ratio, currency).
 * Examples: "317 USD", "SEK 2,966 million", "45%", "1.5x", "3%"
 */
function looksLikeAmount(value: string): boolean {
  return /^\s*([A-Z]{1,4}\s*)?\d/.test(value.trim());
}

/**
 * Extract the most significant numeric token from a formatted value string.
 * "SEK 2,966 million" → "2,966"
 * "317 USD"           → "317"
 * "45%"               → "45"
 * "1.5x"              → "1.5"
 */
function extractNumericToken(s: string): string | null {
  const matches = s.match(/\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?/g);
  if (!matches?.length) return null;
  // Prefer the token with the most digits (most specific)
  return matches.sort(
    (a, b) => b.replace(/\D/g, "").length - a.replace(/\D/g, "").length,
  )[0];
}

/** pdfjs text item shape */
interface PdfjsTextItem {
  str: string;
  transform: number[]; // [scaleX, skewY, skewX, scaleY, x, y]
  width: number;
  height: number;
}

/**
 * Given pdfjs text items and a quote, returns bounding boxes of matched items.
 * Coordinates are in canvas space (origin top-left, pixels).
 *
 * Three strategies tried in order:
 *
 * 1. Percentage  — quote contains "N%"  → exact "N%" token search in raw item strings.
 *    Avoids matching every "3" on the page when the value is "3%".
 *
 * 2. Numeric amount — value looks like a number → extract the main numeric token
 *    from the quote (not value, which may be formatted differently) → exact search.
 *    Quote is raw PDF text so numbers appear exactly as in the document.
 *
 * 3. Phrase — split quote into significant words (>2 chars), slide a window over
 *    consecutive items, find the start position where the most words match in order.
 *    If ≥50% match → return boxes for every item from first to last matched item.
 */
function findQuoteBBoxes(
  items: PdfjsTextItem[],
  quote: string,
  viewport: { width: number; height: number; scale: number },
  value?: string,
): { x: number; y: number; w: number; h: number }[] {
  if (!quote || !items.length) return [];

  const cleanQuote = quote.replace(/\[p\.\s*\d+\]/gi, "").trim();
  if (!cleanQuote) return [];

  const scale = viewport.scale;

  // Shared converter: pdfjs items → canvas bounding boxes
  function toBoxes(its: PdfjsTextItem[]) {
    return its
      .filter((it) => it.str.trim().length > 0)
      .map((item) => {
        const [, , , sy, tx, ty] = item.transform;
        const h = Math.max(Math.abs(sy) * scale, 10);
        return {
          x: tx * scale,
          y: viewport.height - ty * scale - h,
          w: item.width * scale,
          h,
        };
      });
  }

  // ── Strategy 1: Percentage ────────────────────────────────────────────────
  // "3%" is too short to fuzzy-match safely; anchor the search to "N%" exactly.
  const pctToken = cleanQuote
    .match(/\d+(?:\.\d+)?\s*%/)?.[0]
    ?.replace(/\s+/g, "");
  if (pctToken) {
    const matched = items.filter((it) => it.str.includes(pctToken));
    if (matched.length > 0) return toBoxes(matched);
  }

  // ── Strategy 2: Numeric amount ────────────────────────────────────────────
  // Extract from cleanQuote (= raw PDF text), not from `value` (our formatted string).
  // E.g. value = "276,300,000 GBP" but quote has "GBP 276.3 million" → token = "276.3"
  if (value && looksLikeAmount(value)) {
    const token = extractNumericToken(cleanQuote);
    if (token) {
      const tokenNoComma = token.replace(/,/g, "");
      const matched = items.filter(
        (it) =>
          it.str.includes(token) ||
          (tokenNoComma !== token && it.str.includes(tokenNoComma)),
      );
      if (matched.length > 0) return toBoxes(matched);
    }
    // Nothing found numerically — fall through to phrase matching
  }

  // ── Strategy 3: Phrase — word-by-word search ─────────────────────────────
  // Keep only long words (≥5 chars) to skip common/short words ("is", "in", "and"…).
  // Use \b word boundaries so "located" doesn't match "allocated" or "relocated".
  const quoteWords = normalizeText(cleanQuote)
    .split(" ")
    .filter((w) => w.length >= 5);
  if (quoteWords.length === 0) return [];

  const patterns = quoteWords.map((w) => new RegExp(`\\b${w}\\b`));

  const matched = items.filter((it) => {
    const norm = normalizeText(it.str);
    // Skip paragraph-level items: they'd draw a box covering unrelated words
    if (norm.split(/\s+/).filter(Boolean).length > 5) return false;
    return patterns.some((re) => re.test(norm));
  });

  return toBoxes(matched);
}

// ─── Minimal pdfjs proxy interfaces ──────────────────────────────────────────

interface PdfPageProxy {
  getViewport: (opts: { scale: number }) => {
    width: number;
    height: number;
    scale: number;
  };
  getTextContent: () => Promise<{ items: PdfjsTextItem[] }>;
}

interface PdfDocProxy {
  numPages: number;
  getPage: (n: number) => Promise<PdfPageProxy>;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PDFModalProps {
  isOpen: boolean;
  onClose: () => void;
  docId: string;
  fileName?: string;
  page?: number;
  quote?: string;
  rowLabel?: string;
  colLabel?: string;
  value?: string;
}

// ─── PDF fetch hook ───────────────────────────────────────────────────────────

function usePdfBlob(docId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["sandbox-pdf", docId],
    queryFn: async () => {
      const res = await getAuthorized(getPdfUrl(docId));
      if (!res.ok) throw new Error(`PDF not found for doc ${docId}`);
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    enabled: enabled && !!docId,
    // Keep URL alive for the session — do NOT revoke on close (causes red ! on reopen)
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

// ─── Highlight canvas overlay ─────────────────────────────────────────────────

interface HighlightOverlayProps {
  pdfPage: PdfPageProxy | null;
  quote: string;
  canvasWidth: number;
  value?: string;
}

function HighlightOverlay({
  pdfPage,
  quote,
  canvasWidth,
  value,
}: HighlightOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!pdfPage || !quote || !canvasRef.current || canvasWidth <= 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let cancelled = false;

    (async () => {
      // Scale the viewport to match the width react-pdf renders <Page> at
      const naturalViewport = pdfPage.getViewport({ scale: 1 });
      const scale = canvasWidth / naturalViewport.width;
      const viewport = pdfPage.getViewport({ scale });

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (cancelled) return;

      const textContent = await pdfPage.getTextContent();
      if (cancelled) return;

      const items = textContent.items as PdfjsTextItem[];
      const boxes = findQuoteBBoxes(items, quote, viewport, value);

      if (boxes.length > 0) {
        ctx.fillStyle = "rgba(254, 224, 64, 0.55)";
        ctx.strokeStyle = "rgba(234, 179, 8, 0.8)";
        ctx.lineWidth = 1;
        for (const { x, y, w, h } of boxes) {
          const pad = 2;
          ctx.fillRect(x - pad, y - pad, w + pad * 2, h + pad * 2);
          ctx.strokeRect(x - pad, y - pad, w + pad * 2, h + pad * 2);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pdfPage, quote, canvasWidth]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        zIndex: 10,
      }}
    />
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PDFModal({
  isOpen,
  onClose,
  docId,
  fileName,
  page = 1,
  quote,
  rowLabel,
  colLabel,
  value,
}: PDFModalProps) {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(page);
  const [scale, setScale] = useState(1.0);
  const [pdfDoc, setPdfDoc] = useState<PdfDocProxy | null>(null);
  const [pdfPage, setPdfPage] = useState<PdfPageProxy | null>(null);
  const queryClient = useQueryClient();

  const { data: pdfUrl, isLoading, error } = usePdfBlob(docId, isOpen);

  useEffect(() => {
    setCurrentPage(page);
  }, [page, isOpen]);

  // When the PDF doc or current page changes, load the page proxy for highlighting
  useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;
    pdfDoc.getPage(currentPage).then((p) => {
      if (!cancelled) setPdfPage(p);
    });
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, currentPage]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onLoadSuccess = useCallback((pdf: any) => {
    setNumPages(pdf.numPages);
    setPdfDoc(pdf as PdfDocProxy);
  }, []);

  const handleClose = () => {
    setNumPages(0);
    setPdfDoc(null);
    setPdfPage(null);
    onClose();
  };

  const handleForceClose = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      queryClient.removeQueries({ queryKey: ["sandbox-pdf", docId] });
    }
    setNumPages(0);
    setPdfDoc(null);
    setPdfPage(null);
    onClose();
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = fileName ?? `${docId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleOpenNewTab = () =>
    window.open(getPdfUrl(docId), "_blank", "noopener");

  const cleanQuote = quote?.replace(/\[p\.\s*\d+\]/gi, "").trim();
  const pageWidth =
    Math.min(1100, Math.round((window.innerWidth * 0.86) / scale)) * scale;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="full"
      scrollBehavior="inside"
      classNames={{ base: "max-w-[92vw] w-full h-[94vh]" }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-0 pb-2 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0 pr-8">
            <LucideFileText className="h-4 w-4 shrink-0 text-blue-600" />
            <span className="text-sm font-semibold truncate text-gray-800">
              {fileName ?? docId}
            </span>
          </div>
          {pdfUrl && !error && (
            <div className="flex items-center gap-1 pt-2">
              <Button
                isIconOnly
                size="sm"
                variant="flat"
                onPress={() => setScale((s) => Math.max(s - 0.25, 0.5))}
                isDisabled={scale <= 0.5}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-xs text-gray-500 min-w-[2.5rem] text-center tabular-nums">
                {Math.round(scale * 100)}%
              </span>
              <Button
                isIconOnly
                size="sm"
                variant="flat"
                onPress={() => setScale((s) => Math.min(s + 0.25, 3.0))}
                isDisabled={scale >= 3.0}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <div className="w-px h-4 bg-gray-200 mx-1" />
              <Button
                isIconOnly
                size="sm"
                variant="flat"
                onPress={handleDownload}
                title="Download PDF"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                isIconOnly
                size="sm"
                variant="flat"
                color="primary"
                onPress={handleOpenNewTab}
                title="Open in new tab (browser PDF viewer)"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          )}
        </ModalHeader>

        <ModalBody className="px-0 pt-3">
          {(rowLabel || cleanQuote) && (
            <div className="mx-4 mb-3 rounded-lg border border-blue-200 bg-blue-50/70 px-4 py-2.5 flex gap-2 items-start">
              <LucideFileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-blue-500" />
              <div className="flex-1 min-w-0 space-y-1">
                {(rowLabel || (colLabel && value)) && (
                  <p className="text-xs font-semibold text-blue-800 flex flex-wrap gap-x-2 items-baseline">
                    {rowLabel && <span>{rowLabel}</span>}
                    {rowLabel && colLabel && (
                      <span className="text-blue-300">·</span>
                    )}
                    {colLabel && value && (
                      <span className="font-normal text-blue-700">
                        <span className="font-semibold">{colLabel}</span>:{" "}
                        {value}
                      </span>
                    )}
                  </p>
                )}
                {cleanQuote && (
                  <>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-blue-500">
                      Source quote
                    </p>
                    <p className="text-xs text-blue-900 italic leading-relaxed">
                      {cleanQuote}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col items-center min-h-[60vh]">
            {isLoading && (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
                  <p className="text-sm text-gray-500">Loading PDF…</p>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-64">
                <div className="text-center text-red-500">
                  <AlertCircle className="h-12 w-12 mx-auto mb-3" />
                  <p className="text-sm font-medium">PDF unavailable</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {error instanceof Error ? error.message : "Unknown error"}
                  </p>
                </div>
              </div>
            )}

            {pdfUrl && !error && (
              <>
                <div className="flex items-center gap-3 mb-4 bg-white/90 backdrop-blur px-4 py-1.5 rounded-full shadow-sm border border-gray-100 sticky top-0 z-10">
                  <Button
                    size="sm"
                    variant="flat"
                    isIconOnly
                    onPress={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    isDisabled={currentPage <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs font-medium text-gray-700 tabular-nums whitespace-nowrap">
                    Page {currentPage} / {numPages || "…"}
                  </span>
                  <Button
                    size="sm"
                    variant="flat"
                    isIconOnly
                    onPress={() =>
                      setCurrentPage((p) => Math.min(p + 1, numPages))
                    }
                    isDisabled={numPages > 0 && currentPage >= numPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                <Document
                  file={pdfUrl}
                  onLoadSuccess={onLoadSuccess}
                  loading={
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                  }
                  error={
                    <div className="flex items-center justify-center h-64 text-red-500">
                      <AlertCircle className="h-12 w-12 mx-auto" />
                    </div>
                  }
                >
                  {/* Relative wrapper so the canvas overlay sits pixel-perfect over the page */}
                  <div
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    <Page
                      key={`page_${currentPage}_${docId}_${scale}`}
                      pageNumber={currentPage}
                      width={pageWidth}
                      renderTextLayer={true}
                      renderAnnotationLayer={false}
                      className="shadow-xl"
                    />
                    {cleanQuote && (
                      <HighlightOverlay
                        key={`hl_${currentPage}_${docId}_${scale}`}
                        pdfPage={pdfPage}
                        quote={cleanQuote}
                        canvasWidth={pageWidth}
                        value={value}
                      />
                    )}
                  </div>
                </Document>
              </>
            )}
          </div>
        </ModalBody>

        <ModalFooter className="border-t border-gray-100 pt-3">
          <Button color="danger" variant="light" onPress={handleForceClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
